'use client';

import * as Sentry from '@sentry/nextjs';
import { Component, type ReactNode } from 'react';

type State = { crashed: boolean; rethrow: Error | null };

// Set to true by the MutationObserver in GoogleTranslateGuard when an iOS
// browser with native translate (Chrome/CriOS or the Google app/GSA) changes
// html[lang] away from English — indicates translation was activated during
// this page session, not just that the page contains translate="no" spans.
let _iosTranslationActivated = false;

// Set to true by the MutationObserver when desktop Chrome's built-in translate
// adds the translated-ltr / translated-rtl class to <html>. The extension-based
// Google Translate leaves chrome-extension:// stack frames instead; built-in
// translate does not, so we need a separate DOM-class signal.
let _desktopTranslationActivated = false;

export function _setIosTranslationActive(value: boolean) {
  _iosTranslationActivated = value;
}

export function _setDesktopTranslationActive(value: boolean) {
  _desktopTranslationActivated = value;
}

// CriOS = Chrome on iOS. GSA = the Google app (Google Search App) on iOS,
// which embeds its own WebView with a native translate feature — its UA has
// no CriOS token, so it was previously missed entirely by this detector.
function isIosNativeTranslateBrowser(userAgent: string): boolean {
  return userAgent.includes('CriOS/') || userAgent.includes('GSA/');
}

function handleLangMutation(htmlEl: HTMLElement): void {
  if (!isIosNativeTranslateBrowser(navigator.userAgent)) {
    return;
  }
  const lang = htmlEl.lang;
  _iosTranslationActivated = lang !== '' && !lang.startsWith('en');
}

function handleClassMutation(m: MutationRecord, htmlEl: HTMLElement): void {
  const old = m.oldValue ?? '';
  const hadTranslatedClass =
    old.includes('translated-ltr') || old.includes('translated-rtl');
  const hasClass =
    htmlEl.classList.contains('translated-ltr') ||
    htmlEl.classList.contains('translated-rtl');
  if (hadTranslatedClass || hasClass) {
    _desktopTranslationActivated = true;
  }
}

function hasTranslatedClass(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const cl = document.documentElement.classList;
  return cl.contains('translated-ltr') || cl.contains('translated-rtl');
}

export function isGoogleTranslateCrash(error: Error): boolean {
  if (!(error instanceof RangeError)) {
    return false;
  }
  const msg = error.message;
  if (
    !msg.includes('Maximum call stack') &&
    !msg.includes('Too much recursion')
  ) {
    return false;
  }
  const stack = error.stack ?? '';
  const hasExtensionFrame =
    stack.includes('chrome-extension://') || stack.includes('moz-extension://');
  const hasFontInjection =
    typeof document !== 'undefined' &&
    document.body?.querySelector(
      'font.notranslate, font[class*="notranslate"]',
    ) != null;
  // iOS native translate (Chrome/CriOS or the Google app/GSA) changes html[lang]
  // but leaves no extension frames or font.notranslate elements in the DOM.
  // We rely on _iosTranslationActivated (set by a MutationObserver) rather
  // than checking html[lang] or span[translate="no"] directly, because the
  // page itself renders translate="no" spans in components — a DOM query
  // cannot distinguish app-authored spans from browser-injected ones.
  const isIosTranslation =
    typeof navigator !== 'undefined' &&
    isIosNativeTranslateBrowser(navigator.userAgent) &&
    _iosTranslationActivated;
  // Desktop Chrome built-in translate (not the extension) adds translated-ltr /
  // translated-rtl to <html> instead of leaving chrome-extension:// frames.
  // Restrict to Chromium-based browsers (Chrome/ in UA) to avoid suppressing
  // genuine errors on browsers that use these same classes but have unrelated
  // translation implementations (e.g. Yandex with Yandex Translate).
  // Check both the live DOM class (reliable for synchronous crashes) and the
  // MutationObserver-set flag (belt-and-suspenders for async crashes where the
  // class may already have been removed by the time we check).
  const isChromiumBased =
    typeof navigator !== 'undefined' && navigator.userAgent.includes('Chrome/');
  const isDesktopTranslation =
    isChromiumBased && (hasTranslatedClass() || _desktopTranslationActivated);
  return (
    hasExtensionFrame ||
    hasFontInjection ||
    isIosTranslation ||
    isDesktopTranslation
  );
}

export class GoogleTranslateGuard extends Component<
  { children: ReactNode },
  State
> {
  state: State = { crashed: false, rethrow: null };

  private handleWindowError: ((e: ErrorEvent) => void) | null = null;
  private langObserver: MutationObserver | null = null;

  static getDerivedStateFromError(error: Error): State {
    if (isGoogleTranslateCrash(error)) {
      return { crashed: true, rethrow: null };
    }
    return { crashed: false, rethrow: error };
  }

  componentDidCatch(error: Error) {
    if (isGoogleTranslateCrash(error)) {
      Sentry.withScope((scope) => {
        scope.setTag('cause', 'google-translate-mutation');
        scope.setLevel('warning');
        Sentry.captureException(error);
      });
    }
  }

  componentDidMount() {
    const htmlEl = document.documentElement;

    // Sync the iOS flag with the current html[lang] on each mount. Handles the
    // case where the user disabled translation before navigating here (client-side
    // nav keeps the module flag alive across pages, but html[lang] reflects the
    // real current state).
    const currentLang = htmlEl.lang;
    if (!currentLang || currentLang.startsWith('en')) {
      _iosTranslationActivated = false;
    }

    // Sync the desktop flag with the current DOM on each mount.
    _desktopTranslationActivated =
      htmlEl.classList.contains('translated-ltr') ||
      htmlEl.classList.contains('translated-rtl');

    // Watch for html[lang] mutations (iOS native translate) and html[class]
    // mutations (desktop Chrome built-in translate adds translated-ltr / translated-rtl).
    // attributeOldValue: true lets us detect a translated class that was added
    // and removed in the same microtask batch — the live classList would already
    // show it gone, but oldValue still carries the intermediate state.
    // The desktop flag is sticky: once translation was ever observed, keep it
    // true for the life of this page, because an async crash may arrive after
    // the class is gone. componentDidMount re-syncs on each navigation.
    this.langObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'lang') {
          handleLangMutation(htmlEl);
        }
        if (m.attributeName === 'class') {
          handleClassMutation(m, htmlEl);
        }
      }
    });
    this.langObserver.observe(htmlEl, {
      attributes: true,
      attributeFilter: ['lang', 'class'],
      attributeOldValue: true,
    });

    // Catch stack-overflow RangeErrors that escape the React render tree
    // (event handlers, async callbacks) — React error boundaries don't catch those.
    this.handleWindowError = (e: ErrorEvent) => {
      if (e.error instanceof Error && isGoogleTranslateCrash(e.error)) {
        e.preventDefault();
        if (!this.state.crashed) {
          this.setState({ crashed: true, rethrow: null });
          Sentry.withScope((scope) => {
            scope.setTag('cause', 'google-translate-mutation');
            scope.setTag('trigger', 'window-error');
            scope.setLevel('warning');
            Sentry.captureException(e.error);
          });
        }
      }
    };
    window.addEventListener('error', this.handleWindowError);
  }

  componentWillUnmount() {
    this.langObserver?.disconnect();
    if (this.handleWindowError) {
      window.removeEventListener('error', this.handleWindowError);
    }
  }

  render() {
    if (this.state.rethrow) {
      throw this.state.rethrow;
    }
    if (this.state.crashed) {
      return (
        <div className="content-container py-40 text-center">
          <p className="text-ui-fg-subtle">
            This page doesn&apos;t work correctly with browser translation
            active. Please disable translation and{' '}
            <button
              type="button"
              className="underline cursor-pointer hover:no-underline"
              onClick={() => window.location.reload()}
            >
              refresh the page
            </button>
            .
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
